import { Link, useLocation } from "react-router-dom";
import { Facebook, Instagram, Twitter, Youtube, Globe2, Type } from "lucide-react";
import { SITE } from "../lib/site";
import DensityTuner from "./DensityTuner";

const SOCIALS = [
  ["X (Twitter)", "https://x.com/TravelerPulse", Twitter],
  ["Facebook", "https://facebook.com/TravelerPulse", Facebook],
  ["Instagram", "https://instagram.com/travelerpulse", Instagram],
  ["YouTube", "https://youtube.com/@TravelerPulse", Youtube],
];

export default function Footer() {
  const loc = useLocation();
  return (
    <footer className="border-t border-border bg-card mt-20 pb-56 md:pb-0">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2.5 mb-3" data-testid="footer-brand">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary text-primary-foreground font-serif font-800 text-lg shadow-warm-sm">S</span>
            <div className="leading-tight">
              <div className="font-serif font-800 text-lg text-primary tracking-tight">My Social Pulse</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">TravelerPulse.pro</div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">{SITE.tagline}</p>
          <div className="flex items-center gap-2 mt-4" data-testid="footer-socials">
            {SOCIALS.map(([label, url, Icon]) => (
              <a key={label} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}
                data-testid={`footer-social-${label.split(" ")[0].toLowerCase().replace("(", "")}`}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-primary-foreground hover:bg-primary hover:border-primary transition-colors tap">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <FooterCol title="Explore" items={[
          ["Destinations", "/destinations"],
          ["Airports", "/airports"],
          ["Indian Airports", "/india/airports"],
          ["Airlines", "/airlines"],
          ["Aircraft", "/aircraft"],
          ["Countries", "/countries"],
          ["Route Map", "/map"],
        ]} />

        <FooterCol title="Categories" items={[
          ["Air Travel", "/air"],
          ["Rail Travel", "/rail"],
          ["Bus & Coach", "/bus"],
          ["Ground Transport", "/ground"],
          ["Ride Share", "/rideshare"],
          ["Road Travel", "/road"],
          ["Water Travel", "/water"],
          ["Stay", "/stays"],
          ["Visas & Embassies", "/visas"],
        ]} />

        <FooterCol title="My Travel" items={[
          ["My Travel Hub", "/travel-hub"],
          ["Traveler Wallet", "/wallet"],
          ["Membership Plans", "/membership"],
          ["Support \u2764", "/support"],
          ["Feedback", `/feedback?from=${encodeURIComponent(loc.pathname)}`],
        ]} />

        <FooterCol title="Company" items={[
          ["About Us", "/about"],
          ["All Features", "/features"],
          ["About the Founder", "/about/founder"],
          ["Advertise", "/advertise"],
          ["Press Release", "/press"],
          ["Terms of Service", "/legal/terms"],
          ["Privacy Policy", "/legal/privacy"],
        ]} />
      </div>

      <div className="border-t border-border py-2 px-5 text-[11px] text-muted-foreground max-w-7xl mx-auto flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="footer-affiliate-disclosure">
        <Link to="/legal/affiliate-disclosure" className="hover:text-primary underline decoration-dotted underline-offset-4">
          Advertising & Affiliate Disclosure
        </Link>
        <span className="text-muted-foreground/60">·</span>
        <span>Compensation may influence placement; scores are ours alone.</span>
      </div>

      <div className="border-t border-border py-4 px-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground font-mono max-w-7xl mx-auto">
        <span className="flex items-center gap-2"><Globe2 className="w-3.5 h-3.5 text-accent" /> © 2026 {SITE.name} · TravelerPulse.pro</span>
        <div className="flex items-center gap-4 flex-wrap justify-center">
          <span className="inline-flex items-center gap-1"><Type className="w-3.5 h-3.5 text-accent" /><DensityTuner testid="footer-density" compact /></span>
          <button onClick={() => {
            try {
              window.open(window.location.origin + "/?native=1", "msp-native-preview",
                "width=400,height=860,menubar=no,toolbar=no,location=no,status=no,resizable=yes");
            } catch { /* silent */ }
          }} data-testid="footer-preview-app" className="hover:text-primary transition-colors font-mono">Preview as app</button>
          <Link to="/get-app" className="hover:text-primary transition-colors font-mono" data-testid="footer-get-app-link">Install the app</Link>
          <Link to="/contact" className="hover:text-primary transition-colors" data-testid="footer-contact-link">Contact</Link>
        </div>
      </div>
    </footer>
  );
}

const FooterCol = ({ title, items }) => (
  <div>
    <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-accent mb-3 font-700">{title}</h4>
    <ul className="space-y-2.5">
      {items.map(([label, to]) => (
        <li key={label}><Link to={to} className="text-sm text-foreground/80 hover:text-primary transition-colors">{label}</Link></li>
      ))}
    </ul>
  </div>
);
